import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../services/Firebase.js";
import { useUser } from "../context/UserContext.tsx";
import { useBetContext } from "../context/BetContext.tsx";
import { useBetContext as useParlayBetContext } from "../context/ParlayBetContext.tsx";
import { useModal } from "../context/ModalContext.tsx";
import {
    clearAppBackgrounded,
    clearBrowserClosed,
    clearBrowserSessionAlive,
    clearSessionExpiredPending,
    clearVoluntaryLogout,
    hasBrowserSessionAlive,
    isBackgroundSessionExpired,
    isSessionExpiredAck,
    isSessionExpiredPending,
    isVoluntaryLogout,
    markAppBackgrounded,
    markSessionExpiredAck,
} from "../helpers/browserSession.ts";

const PUBLIC_PATHS = ["/", "/pass"];

const isPublicPath = (path: string) =>
    PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

const hasStoredUser = (): boolean => {
    try {
        return Boolean(localStorage.getItem("user"));
    } catch {
        return false;
    }
};

const hasDeadSessionEvidence = (): boolean =>
    isSessionExpiredPending() ||
    hasStoredUser() ||
    Boolean(auth.currentUser);

type EvaluateSource = "boot" | "resume" | "route";

/**
 * - Browser/tab closed → session dies (sessionStorage cleared by the browser).
 * - Refresh keeps sessionStorage → session stays alive.
 * - Away in background > 15 min → session expires only on real resume (not while navigating).
 * - Manual logout → no loop on the login screen.
 * - Never shows a modal; always clears session and redirects to login.
 */
const SessionResumeGuard: React.FC = () => {
    const { user, firebaseUser, logout, isLoading } = useUser();
    const { clearBets, setUseFreePlay } = useBetContext();
    const {
        deleteAllBets,
        setTeaserPoints,
        setTeaserType,
        setTeaserFixture,
        setErrorMessageBets,
    } = useParlayBetContext();
    const { closeModal } = useModal();
    const navigate = useNavigate();
    const location = useLocation();
    const handlingRef = useRef(false);

    const clearOpenBetsAndModals = useCallback(() => {
        clearBets();
        setUseFreePlay(false);
        deleteAllBets();
        setTeaserPoints(0);
        setTeaserType(null);
        setTeaserFixture('');
        setErrorMessageBets('');
        closeModal();
    }, [
        clearBets,
        setUseFreePlay,
        deleteAllBets,
        setTeaserPoints,
        setTeaserType,
        setTeaserFixture,
        setErrorMessageBets,
        closeModal,
    ]);

    const expireSession = useCallback(async (_reason: "closed" | "background") => {
        if (handlingRef.current || isVoluntaryLogout() || isSessionExpiredAck()) return;

        handlingRef.current = true;

        markSessionExpiredAck();
        clearBrowserSessionAlive();
        clearAppBackgrounded();
        clearBrowserClosed();
        clearSessionExpiredPending();
        clearOpenBetsAndModals();

        try {
            await logout({ voluntary: false });
        } finally {
            // Already on login — navigating again remounts and causes the bounce.
            if (!isPublicPath(window.location.pathname)) {
                navigate("/", { replace: true });
            }
            handlingRef.current = false;
        }
    }, [logout, navigate, clearOpenBetsAndModals]);

    const evaluateSession = useCallback((source: EvaluateSource = "route") => {
        if (isVoluntaryLogout() || isSessionExpiredAck()) return;

        const alive = hasBrowserSessionAlive();

        if (alive) {
            clearBrowserClosed();
            clearSessionExpiredPending();
            if (isPublicPath(window.location.pathname)) {
                clearVoluntaryLogout();
                clearAppBackgrounded();
                return;
            }

            // Background expiry ONLY when returning to the app — never on in-app navigation.
            if (source === "resume" && isBackgroundSessionExpired()) {
                void expireSession("background");
                return;
            }

            if (document.visibilityState === "visible") {
                clearAppBackgrounded();
            }
            return;
        }

        // Dead tab session with prior login evidence → logout and go to login (no modal).
        const evidence = hasDeadSessionEvidence() || Boolean(user || firebaseUser);
        if (!evidence) {
            if (isPublicPath(window.location.pathname)) {
                clearVoluntaryLogout();
                clearAppBackgrounded();
                clearBrowserClosed();
            }
            return;
        }

        void expireSession("closed");
    }, [user, firebaseUser, expireSession]);

    // Run ASAP — do not block on isLoading for dead-session detection.
    useEffect(() => {
        if (isVoluntaryLogout() || isSessionExpiredAck()) return;

        if (!hasBrowserSessionAlive() && hasDeadSessionEvidence()) {
            evaluateSession("boot");
            return;
        }

        if (isLoading) return;
        evaluateSession("route");
    }, [
        isLoading,
        location.pathname,
        user,
        firebaseUser,
        evaluateSession,
    ]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                if (hasBrowserSessionAlive() && !isVoluntaryLogout()) {
                    markAppBackgrounded();
                }
                return;
            }

            if (document.visibilityState === "visible") {
                evaluateSession("resume");
            }
        };

        const onPageHide = () => {
            if (hasBrowserSessionAlive() && !isVoluntaryLogout()) {
                markAppBackgrounded();
            }
        };

        const onPageShow = (_event: PageTransitionEvent) => {
            // Full reload on bfcache restore causes a visible bounce on login.
            // Re-evaluate session in place instead.
            if (isVoluntaryLogout() || isSessionExpiredAck()) return;
            evaluateSession("resume");
        };

        const onFreeze = () => {
            if (hasBrowserSessionAlive() && !isVoluntaryLogout()) {
                markAppBackgrounded();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);
        document.addEventListener("freeze", onFreeze);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
            document.removeEventListener("freeze", onFreeze);
        };
    }, [evaluateSession]);

    return null;
};

export default SessionResumeGuard;
